import { useState } from "react";
import { MailWarning } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";

/**
 * Increment 23 + QA #50: a slim banner nudging signed-in users to verify their email.
 * Two paths: a one-click verification link, or a 6-digit OTP code (emailed from the
 * SNP-domain sender) the user enters inline. Not dismissible until verified.
 */
export function VerifyBanner() {
  const { user, refresh, setUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [link, setLink] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // OTP path
  const [otpMode, setOtpMode] = useState(false);
  const [code, setCode] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [, setOtpSent] = useState(false);
  const [devCode, setDevCode] = useState<string | null>(null);

  if (!user || user.emailVerified || user.userType === "admin") return null;

  async function send() {
    setBusy(true); setErr(null);
    try {
      const r = await api.sendVerifyEmail();
      if (r?.alreadyVerified) { await refresh(); setSent(false); setLink(null); return; }
      setSent(true);
      if (r?.verifyUrl) setLink(r.verifyUrl);
    } catch (e: any) { setErr(e?.message || "Couldn't send right now."); }
    finally { setBusy(false); }
  }

  async function sendCode() {
    setOtpBusy(true); setErr(null);
    try {
      const r = await api.sendEmailOtp();
      if (r?.alreadyVerified) { await refresh(); return; }
      setOtpMode(true); setOtpSent(true);
      if (r?.devCode) setDevCode(r.devCode);
    } catch (e: any) { setErr(e?.message || "Couldn't send the code."); }
    finally { setOtpBusy(false); }
  }

  async function verifyCode() {
    if (!/^\d{6}$/.test(code.trim())) { setErr("Enter the 6-digit code."); return; }
    setOtpBusy(true); setErr(null);
    try {
      const r = await api.verifyEmailOtp(code.trim());
      if (r?.user) setUser(r.user); else await refresh();
    } catch (e: any) { setErr(e?.message || "That code didn't work."); }
    finally { setOtpBusy(false); }
  }

  const relLink = link ? link.replace(/^https?:\/\/[^/]+/, "") : null;

  return (
    <section aria-label="Email verification" className="bg-amber-50 border-b border-amber-200">
      <div className="container-x grid grid-cols-[1rem_minmax(0,1fr)] items-start gap-x-3 gap-y-2 py-2 text-sm text-amber-900 sm:flex sm:flex-wrap sm:items-center sm:gap-3">
        <MailWarning className="mt-0.5 h-4 w-4 shrink-0 sm:mt-0" aria-hidden="true" />
        <div className="min-w-0 break-words sm:flex-1">
          {!otpMode && !sent && <span>Verify your email to unlock posting, bidding and orders.</span>}
          {!otpMode && sent && !relLink && <span>Verification request submitted for <b>{user.email}</b>. Check your inbox if email delivery is configured.</span>}
          {!otpMode && sent && relLink && (
            <span>UAT test shortcut (in production this link is emailed only to <b>{user.email}</b>) —{" "}
              <a href={relLink} className="font-semibold underline">confirm your email now</a>.</span>
          )}
          {otpMode && <span>We emailed a 6-digit code to <b>{user.email}</b>{devCode ? <> (UAT code: <b>{devCode}</b>)</> : ""}. Enter it to verify.</span>}
          {err && <span className="text-brand-red"> {err}</span>}
        </div>

        {otpMode ? (
          <div className="col-span-2 flex min-w-0 flex-wrap items-center justify-end gap-2">
            <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="123456"
              aria-label="Email verification code"
              className="w-24 h-7 rounded border border-amber-300 px-2 text-sm tracking-widest text-center bg-white" />
            <button onClick={verifyCode} disabled={otpBusy || code.length < 6} className="btn-ghost h-7 px-3 text-xs font-semibold border border-amber-300 disabled:opacity-60">{otpBusy ? "Verifying…" : "Verify"}</button>
            <button onClick={sendCode} disabled={otpBusy} className="text-xs underline text-amber-900/80">Resend</button>
          </div>
        ) : (
          <div className="col-span-2 flex min-w-0 flex-wrap items-center justify-end gap-2">
            {!sent && (
              <button onClick={send} disabled={busy} className="btn-ghost h-7 px-3 text-xs font-semibold border border-amber-300 disabled:opacity-60">{busy ? "Sending…" : "Email me a link"}</button>
            )}
            <button onClick={sendCode} disabled={otpBusy} className="btn-ghost h-7 px-3 text-xs font-semibold border border-amber-300 disabled:opacity-60">{otpBusy ? "Sending…" : "Verify by code"}</button>
          </div>
        )}
      </div>
    </section>
  );
}
